import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { useAdjustStock } from '../../hooks';
import type { ToastSeverity } from '../../lib/useToast';
import { useLocationLookup } from '../../lib/useLocationLookup';
import { STOCK_ADJUSTMENT_REASON_OPTIONS } from '../../model/options';
import type { StockAdjustmentMovementType } from '../../model/types';
import { LocationSelect } from './LocationSelect';

export interface AdjustStockDialogProps {
  open: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

function toSignedNumberOrUndefined(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  return Number.isNaN(n) || n === 0 ? undefined : n;
}

/**
 * Records shrinkage, damage, theft, a found-item recovery, or a manual correction
 * (page-level action). The reason select maps to `AdjustStockRequest.movementType`, restricted
 * to the ADJUSTMENT_* values — the only ones the BFF accepts here.
 */
export function AdjustStockDialog({ open, onClose, onResult }: AdjustStockDialogProps) {
  const { t } = useTranslation('inventory');
  const adjust = useAdjustStock();
  const { locations } = useLocationLookup();

  const [productId, setProductId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [movementType, setMovementType] =
    useState<StockAdjustmentMovementType>('ADJUSTMENT_SHRINKAGE');
  const [quantityDelta, setQuantityDelta] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setProductId('');
      setLocationId('');
      setMovementType('ADJUSTMENT_SHRINKAGE');
      setQuantityDelta('');
      setNote('');
    }
  }, [open]);

  const quantityDeltaValue = toSignedNumberOrUndefined(quantityDelta);
  const valid = productId.trim() !== '' && locationId !== '' && quantityDeltaValue !== undefined;

  const submit = () => {
    if (!valid || quantityDeltaValue === undefined) return;
    adjust.mutate(
      {
        productId: productId.trim(),
        locationId,
        movementType,
        quantityDelta: quantityDeltaValue,
        note: note.trim() || undefined,
      },
      {
        onSuccess: () => {
          onResult('success', t('adjustStockDialog.success'));
          onClose();
        },
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('adjustStockDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label={t('adjustStockDialog.productIdLabel')}
            required
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            error={productId.trim() === ''}
            autoFocus
            fullWidth
            slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
          />
          <LocationSelect
            label={t('adjustStockDialog.locationLabel')}
            value={locationId}
            onChange={setLocationId}
            locations={locations}
            required
            error={locationId === ''}
            fullWidth
          />
          <TextField
            select
            label={t('adjustStockDialog.reasonLabel')}
            value={movementType}
            onChange={(e) => setMovementType(e.target.value as StockAdjustmentMovementType)}
            fullWidth
          >
            {STOCK_ADJUSTMENT_REASON_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {t(o.label)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('adjustStockDialog.quantityDeltaLabel')}
            type="number"
            required
            value={quantityDelta}
            onChange={(e) => setQuantityDelta(e.target.value)}
            error={quantityDelta.trim() !== '' && quantityDeltaValue === undefined}
            helperText={t('adjustStockDialog.quantityDeltaHelper')}
            fullWidth
            slotProps={{ htmlInput: { step: 'any' } }}
          />
          <TextField
            label={t('adjustStockDialog.noteLabel')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={adjust.isPending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || adjust.isPending}
          startIcon={adjust.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
